import { Component } from 'react';
import { connect } from 'react-redux';
import { Route, Switch, withRouter } from 'react-router-dom';
import Tooltip from 'react-tooltip';
import queryString from 'query-string';
import TagManager from 'react-gtm-module';

import tokenApi from 'api/tokens';
import api from 'api/user';
import { initApp } from 'actions/app';
import getWl from 'api/wl';
import { enableApiLogs } from 'api/logs';
import { RootState, StoreProps } from 'store';

import { ErrorBoundary } from 'components/errorBoundary';
import NotificationContainer from 'components/ui/notification/NotificationContainer';
import ModalContainer from 'components/modal/ModalContainer';
import TooltipInfo from 'components/ui/tooltipInfo';
import AppContainer from 'pages/app/AppContainer';
import PolicyWidget from 'pages/app/components/policyWidget';

import publicRoutes from 'config/publicRoutes';
import LocalStorage from 'helpers/LocalStorage';
import { addListeners } from 'decorators/addListeners';
import axiosMiddleware from 'helpers/axiosMiddleware';
import Token from 'helpers/Token';
import Utils from 'helpers/Utils';
import Env from 'helpers/Env';
import Repository from 'helpers/Repository';
import Messages from 'constants/rpcTypes';
import TooltipHtml from 'components/ui/tooltipInfo/TooltipHtml';
import { WithRouterProps } from 'decorators/withRouter';
import { setAppTheme } from 'theme';
import { wl } from 'actions/settings';
import { activeWlConfigStore } from 'config/wls/wlConfig';
import { Settings } from 'reducers/settings';
import { getThemeConfig } from 'api/dictionaries';
import { LocalizationProvider } from 'i18n/LocalizationProvider';
import Loader from 'components/ui/loader';
import { bootstrapWl } from 'runtime/bootstrap';
import { preloadWl } from 'runtime/preload';

interface ConnectedProps {
  user: RootState['user'];
}

type Props = ConnectedProps & StoreProps & WithRouterProps;

interface State {
  instanceId: string;
  canRenderApp: boolean;
}

@addListeners([Messages.Redirect, Messages.Auth_LoginToken])
class StartupContainer extends Component<Props, State> {
  constructor(props: Props) {
    super(props);

    const instanceId = Utils.getHash();

    this.state = {
      instanceId,
      canRenderApp: false,
    };

    this.attachEvents();
    Repository.set('history', this.props.history);
    axiosMiddleware({ instanceId });
  }

  componentDidMount() {
    const { history } = this.props;
    const { redirect_token: token } = queryString.parse(
      history.location.search
    );

    if (token) {
      history.replace({});
      tokenApi.updateToken(token);
    } else {
      this.startApp();
    }
  }

  componentDidUpdate(prevProps: Readonly<Props>): void {
    const { location } = this.props;

    if (prevProps.location.pathname !== location.pathname) {
      Tooltip.hide();
    }
  }

  componentWillUnmount() {
    this.detachEvents();
  }

  startApp = async () => {
    const { dispatch } = this.props;
    const { instanceId } = this.state;

    dispatch(initApp({ instanceId }));

    await Promise.all([
      this.fetchWl(),
      this.fetchUser(),
    ]);

    this.setGlobalWindowProperties();
    this.setAppInstanceId();
    this.setState({ canRenderApp: true });
  };

  setAppInstanceId = () => {
    const { instanceId } = this.state;
    if (instanceId) {
      LocalStorage.set('instanceId', instanceId);
    }
  };

  attachEvents = () => {
    window.addEventListener('focus', this.setAppInstanceId);
  };

  detachEvents = () => {
    window.removeEventListener('focus', this.setAppInstanceId);
  };

  fetchUser = async () => {
    const { history } = this.props;
    const { location } = history;
    const { authToken } = Token.getTokens();
    const pathname = history.location.pathname;
    const isPublicRoute = !!publicRoutes.find(
      (route) => route.path === pathname
    );
    const isLoginPage = pathname === '/login';

    if (!isPublicRoute) {
      if (authToken) {
        try {
          await api.getUser();
        } catch (error) {
          console.error(error);
        }
      } else {
        history.replace('/login', {
          returnUrl: location.pathname + location.search,
        });
      }
    } else if (isLoginPage) {
      if (authToken) {
        try {
          await api.getUser();
          history.replace('/');
        } catch (error) {
          console.error(error);
        }
      }
    }
  };

  fetchWl = async () => {
    const { dispatch } = this.props;

    try {
      const { brandConfig, ...rest } = await getWl();
      const wlData = {
        ...rest,
        brandConfig: await this.setBrandConfig(brandConfig),
      };

      this.setAppMetaData(wlData);
      dispatch(wl(wlData));
      await activeWlConfigStore.setActiveWlConfig(wlData.wlId);
      this.setWlBodyClass(wlData.wlId);

      await bootstrapWl(wlData.wlId);
      await preloadWl(wlData.wlId);

      Repository.set('wlId', wlData.wlId);

      return wlData;

    } catch (error) {
      console.error(error);
      throw error;
    }
  };

  setBrandConfig = async (incomingBrandConfig: Settings['brandConfig']) => {
    const localBrandConfig = LocalStorage.get('brandConfig');

    const currentBrandConfig = await this.determineCurrentBrandConfig(
      localBrandConfig,
      incomingBrandConfig
    );

    // setting app font family by language preferences
    const theme = currentBrandConfig.config.theme;
    const interfaceLang = LocalStorage.get('noAuthLang');
    const fontFamily =
      theme.fontFamily[interfaceLang] ?? theme.fontFamily.default;

    setAppTheme({
      ...theme,
      fontFamily,
    });

    return currentBrandConfig;
  };

  determineCurrentBrandConfig = async (
    localBrandConfig: Settings['brandConfig'],
    incomingBrandConfig: Settings['brandConfig']
  ) => {
    if (!localBrandConfig) {
      return incomingBrandConfig;
    }

    const { brandConfigVersion: localVersion } = localBrandConfig;
    const { brandConfigVersion: incomingVersion } = incomingBrandConfig;

    if (localVersion === incomingVersion) {
      return localBrandConfig;
    }

    const updatedBrandConfig = await getThemeConfig(localBrandConfig.id);
    LocalStorage.set('brandConfig', updatedBrandConfig);
    return updatedBrandConfig;
  };

  setGlobalWindowProperties = () => {
    if (Env.isProduction()) {
      window['appVersion'] = Env.getAppVersion();
      window['currentBuilder'] = Env.getCurrentBuilder();
    }

    window['enableApiLogs'] = enableApiLogs;
  };

  setAppMetaData = (wlData) => {
    const { htmlDocumentTitle, htmlDocumentDescription, brandConfig, gtmId, wlId } =
      wlData;
    const { faviconUrl } = brandConfig.config.assets;

    const meta = document.querySelector('meta[name=description]');
    const link = document.querySelector('link[rel*=icon]');

    if (htmlDocumentTitle) {
      document.title = htmlDocumentTitle;
    }

    if (meta && htmlDocumentDescription) {
      meta.setAttribute('content', htmlDocumentDescription);
    }

    if (link && faviconUrl) {
      link.setAttribute('href', faviconUrl);
    }

    if (gtmId) {
      TagManager.initialize({ gtmId });
    }

    this.setWlBodyClass(wlId);
  };

  setWlBodyClass = (wlId: number | null) => {
    const config = activeWlConfigStore.getActiveWlConfig();

    if (wlId && config?.isNewDesignEnabled) {
      document.body.classList.add(`wl${wlId}-ui`);
    }
  };

  onEvent = async ({ data, name }) => {
    if (name === Messages.Redirect) {
      const { location } = window;
      let redirectUrl = data.payload.redirectUrl;

      if (Env.isDevelopment()) {
        const { pathname, search } = new URL(redirectUrl);
        redirectUrl = location.origin + pathname + search;
      }

      location.replace(redirectUrl);
    } else if (name === Messages.Auth_LoginToken) {
      await this.startApp();
    }
  };

  render() {
    const { user } = this.props;
    const { canRenderApp } = this.state;

    return canRenderApp ? (
      <>
        <ErrorBoundary>
          <LocalizationProvider>
            <div className='layout-app'>
              <Switch>
                {publicRoutes.map((route, index) => {
                  const PublicComponent = route.component;

                  return (
                    <Route
                      key={`${route.path}${index}`}
                      exact={route.exact}
                      path={route.path}
                      render={(props) => {
                        return <PublicComponent {...props} user={user} />;
                      }}
                    />
                  );
                })}
                <Route path={'/'} render={() => <AppContainer />} />
              </Switch>

              <PolicyWidget />
              <ModalContainer />
              <TooltipInfo />
              <TooltipHtml />
            </div>
          </LocalizationProvider>
        </ErrorBoundary>
        <NotificationContainer />
      </>
    ) : (
      <Loader />
    );
  }
}

const mapStateToProps = (state: RootState): ConnectedProps => ({
  user: state.user,
});

export default withRouter(connect(mapStateToProps)(StartupContainer));
